import type { Dispatch, SetStateAction } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useSnackbar } from "notistack"

import { UserDetails } from "../../../modules/nonAdminUser/nonAdminUserApi"
import { UserPaymentAccountQuery } from "../../../modules/nonAdminUser/reports/home/components/wallets/walletsApi"
import { UserWeekMenu } from "../../../modules/nonAdminUser/reports/home/weekMenuApi"
import { UpcomingOrdersMeetingService } from "../../../modules/nonAdminUser/reports/upcomingOrders/meetingService/meetingServiceApi"
import type { OutlookBookingSuccessArgs } from "../../../modules/order/pages/outlookCreateOrderTypes"

import { ConfigQuery } from "./createOrderApi"
import { CreateOrderDialogController } from "./CreateOrderDialogController"
import { CreateOrderDialogMode, CreateOrderDialogType } from "./createOrderTypes"
import type { CreateOrderDialogFlow } from "./createOrderTypes"
import type { CreateOrderType } from "./helpers"
import type { OnBehalfCompletedDialog } from "./hooks/useCreateOrderUser"
import { OrderOnBehalfFlow } from "./orderOnBehalf/OrderOnBehalfFlow"

// Queries whose results are affected by creating/closing an order; refresh them on close.
const QUERY_KEYS_TO_INVALIDATE_ON_CLOSE: ReadonlyArray<string> = [
    UserWeekMenu.MEETING_SERVICE_LIST,
    UserDetails.USER_CALENDAR_EVENTS,
    ConfigQuery.EVENTS_LIST_BY_DAY,
    UpcomingOrdersMeetingService.UPCOMING_ORDERS_MEETING_SERVICE,
    UserWeekMenu.USER_POS_PURCHASES,
    UserPaymentAccountQuery.USER_PAYMENT_ACCOUNT,
]

type CreateOrderDialogProps = Readonly<{
    open: boolean
    onClose: (val: boolean) => void
    activeOrderTab?: CreateOrderType
    dialogType?: CreateOrderDialogType
    orderId?: number
    mode: CreateOrderDialogMode
    flowConfig?: CreateOrderDialogFlow
    handleOutlookBookingCompleted?: (args: OutlookBookingSuccessArgs) => void
    isOnBehalfCompleted: OnBehalfCompletedDialog
    setIsOnBehalfCompleted: Dispatch<SetStateAction<OnBehalfCompletedDialog>>
}>

export function CreateOrderDialog({
    open,
    onClose,
    activeOrderTab,
    dialogType = CreateOrderDialogType.ORDER,
    orderId,
    mode = CreateOrderDialogMode.CREATE,
    flowConfig,
    handleOutlookBookingCompleted,
    isOnBehalfCompleted,
    setIsOnBehalfCompleted,
}: CreateOrderDialogProps) {
    const queryClient = useQueryClient()
    const { closeSnackbar } = useSnackbar()

    const handleOnComplete = (id: number, name: string, companyId: number) => {
        setIsOnBehalfCompleted((prev) => ({ ...prev, show: false, userId: id, displayName: name, companyId }))
    }

    const handleClose = () => {
        closeSnackbar()
        onClose(false)

        QUERY_KEYS_TO_INVALIDATE_ON_CLOSE.forEach((queryKey) => {
            queryClient.invalidateQueries({ queryKey: [queryKey] })
        })

        // Free/busy schedule won't refetch on a plain invalidate, so force it for all observers.
        queryClient.invalidateQueries({
            queryKey: [UserWeekMenu.USER_MEETING_ORDER_FREE_BUSY_SCHEDULE],
            refetchType: "all",
        })

        // Note: the extra-services cache is cleared when the dialog opens (CreateOrderDialogHost),
        // which covers every close path — including browser/mouse back that bypasses handleClose.
    }

    return isOnBehalfCompleted.show ? (
        <OrderOnBehalfFlow
            userType={isOnBehalfCompleted.userType}
            onComplete={handleOnComplete}
            handleCloseParentDialog={handleClose}
        />
    ) : (
        <CreateOrderDialogController
            open={open}
            orderId={orderId}
            mode={mode}
            flowConfig={flowConfig}
            handleClose={handleClose}
            activeOrderTab={activeOrderTab}
            dialogType={dialogType}
            isOnBehalfCompleted={isOnBehalfCompleted}
            setIsOnBehalfCompleted={setIsOnBehalfCompleted}
            handleOutlookBookingCompleted={handleOutlookBookingCompleted}
        />
    )
}
